/**
 * キャラクター(docs/20 §5・docs/19 §3 CharacterDef・§9 nameParts)。
 * 一覧: サムネ / 名前 / リグ / 科目。
 * 編集: 名前、枠(リグの既定から選ぶ・並べ替え・名前・必須)、初期の見た目(starter から選ぶ)、状態、
 * 名前の部品(prefixes / nouns。id は自動採番で、作った後は変えない)。
 */
import { useMemo, useState } from 'react';
import {
  CHARACTER_STATUSES,
  RIGS,
  RIG_TYPES,
  type CharacterDef,
  type CharacterLook,
  type ItemDef,
  type LocalizedWord,
  type RigType,
  type SlotDef,
} from '@paltyping/content-schema';
import {
  fieldIssues,
  issuesFor,
  nextWordId,
  starterItems,
  validateDrafts,
  newCharacterDef,
  newIdProblem,
  nextOrder,
  type CatalogDrafts,
} from '../../services/catalog';
import {
  CatalogSummary,
  IssueList,
  ReadOnlyNote,
  SaveNotice,
  type CatalogStore,
  type SaveOutcome,
} from './common';
import { PixelPreview } from './PixelPreview';

export const CHARACTER_STATUS_LABELS: Readonly<Record<CharacterDef['status'], string>> = {
  active: '有効',
  paused: '休止',
  retired: '廃止',
};

interface Props {
  readonly store: CatalogStore;
  readonly editable: boolean;
  readonly contentBase: string | null;
}

function Thumb({
  character,
  itemsById,
  contentBase,
}: {
  readonly character: CharacterDef;
  readonly itemsById: ReadonlyMap<string, ItemDef>;
  readonly contentBase: string | null;
}) {
  if (character.rigType === 'pixel-layered') {
    return (
      <PixelPreview
        contentBase={contentBase}
        equipped={character.defaultLook.equipped}
        itemsById={itemsById}
        scale={1}
        label={`${character.id} のサムネ`}
      />
    );
  }
  const base =
    character.bases?.find((entry) => entry.id === character.defaultLook.base) ??
    character.bases?.[0];
  return <span className="emoji-thumb">{base?.emoji ?? '?'}</span>;
}

export function CharactersPanel({ store, editable, contentBase }: Props) {
  const { drafts } = store;
  const [selectedId, setSelectedId] = useState<string | null>(drafts.character[0]?.id ?? null);
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState<SaveOutcome | null>(null);
  const summary = useMemo(() => validateDrafts(drafts), [drafts]);
  const itemsById = useMemo(
    () => new Map(drafts.item.map((entry) => [entry.id, entry.data.def])),
    [drafts.item],
  );
  const selected = drafts.character.find((entry) => entry.id === selectedId);

  const [creating, setCreating] = useState<CharacterDef | null>(null);

  async function save(def: CharacterDef): Promise<void> {
    setBusy(true);
    setOutcome(null);
    const existing = drafts.character.find((entry) => entry.id === def.id);
    // 新規は expectedRevision 0(buildSaveOps が下書きに無いものを 0 にする)。
    const order = existing ? existing.data.order : nextOrder(drafts.character);
    const result = await store.save([{ docType: 'character', id: def.id, data: { order, def } }]);
    setOutcome(result);
    if (result.ok && creating?.id === def.id) {
      setCreating(null);
      setSelectedId(def.id);
    }
    setBusy(false);
  }

  return (
    <div className="catalog-split">
      <div className="pane pane--list">
        <div className="toolbar">
          <h2 className="section-title">キャラクター</h2>
          <CatalogSummary errors={summary.errors.length} warnings={summary.warnings.length} />
        </div>
        <table className="table" aria-label="キャラクター一覧">
          <thead>
            <tr>
              <th>サムネ</th>
              <th>名前</th>
              <th>リグ</th>
              <th>科目</th>
            </tr>
          </thead>
          <tbody>
            {drafts.character.map((entry) => (
              <tr
                key={entry.id}
                className={entry.id === selectedId && !creating ? 'row--active' : undefined}
                onClick={() => {
                  setOutcome(null);
                  setCreating(null);
                  setSelectedId(entry.id);
                }}
              >
                <td>
                  <Thumb
                    character={entry.data.def}
                    itemsById={itemsById}
                    contentBase={contentBase}
                  />
                </td>
                <td>
                  {entry.data.def.name.ja || <span className="muted">(未設定)</span>}
                  <div className="mono muted">{entry.id}</div>
                </td>
                <td className="mono">{entry.data.def.rigType}</td>
                <td className="mono">
                  {drafts.subject
                    .filter((subject) => subject.data.def.characterId === entry.id)
                    .map((subject) => subject.id)
                    .join(', ') || '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {editable && (
          <NewCharacterForm
            drafts={drafts}
            onCreate={(def) => {
              setOutcome(null);
              setCreating(def);
            }}
          />
        )}
      </div>
      <div className="pane">
        <SaveNotice outcome={outcome} onReload={() => void store.reload()} />
        {creating ? (
          <CharacterEditor
            key={`new:${creating.id}`}
            initial={creating}
            isNew
            onCancel={() => {
              setCreating(null);
            }}
            store={store}
            editable={editable}
            busy={busy}
            itemsById={itemsById}
            contentBase={contentBase}
            onSave={save}
          />
        ) : selected ? (
          <CharacterEditor
            key={`${selected.id}:${selected.revision}`}
            initial={selected.data.def}
            store={store}
            editable={editable}
            busy={busy}
            itemsById={itemsById}
            contentBase={contentBase}
            onSave={save}
          />
        ) : (
          <p className="muted">左の一覧からキャラクターを選んでください。</p>
        )}
      </div>
    </div>
  );
}

/** キャラクターの追加: ID・名前・リグを選ぶ(枠はリグの既定、休止、初期の見た目・名前の部品は空)。 */
function NewCharacterForm({
  drafts,
  onCreate,
}: {
  readonly drafts: CatalogDrafts;
  readonly onCreate: (def: CharacterDef) => void;
}) {
  const [id, setId] = useState('');
  const [name, setName] = useState('');
  const [rigType, setRigType] = useState<RigType>('pixel-layered');
  const trimmedId = id.trim();
  const problem = newIdProblem(trimmedId, drafts.character);
  return (
    <section className="card" aria-label="キャラクターを追加">
      <h3>キャラクターを追加</h3>
      <div className="toolbar toolbar--wrap">
        <input
          className="input mono"
          aria-label="新しいキャラクターのID"
          placeholder="ID(例 kokugo-hero)"
          value={id}
          onChange={(event) => {
            setId(event.target.value);
          }}
        />
        <input
          className="input"
          aria-label="新しいキャラクターの名前"
          placeholder="名前(ja)"
          value={name}
          onChange={(event) => {
            setName(event.target.value);
          }}
        />
        <select
          className="select select--inline"
          aria-label="新しいキャラクターのリグ"
          value={rigType}
          onChange={(event) => {
            setRigType(event.target.value as RigType);
          }}
        >
          {RIG_TYPES.map((type) => (
            <option key={type} value={type}>
              {type}
            </option>
          ))}
        </select>
        <button
          className="button button--small"
          type="button"
          disabled={problem !== null}
          onClick={() => {
            // legacy-emoji の素体は既存の legacy-emoji キャラクターから引き継ぐ(画面で編集できないため)。
            const bases =
              drafts.character.find((entry) => entry.data.def.rigType === 'legacy-emoji')?.data.def
                .bases ?? [];
            onCreate(newCharacterDef(trimmedId, name.trim(), rigType, bases));
            setId('');
            setName('');
          }}
        >
          作成
        </button>
      </div>
      {trimmedId !== '' && problem !== null && (
        <p className="field-hint field-hint--error" role="alert">
          {problem}
        </p>
      )}
      <p className="muted">
        休止(paused)で作ります。保存するまで下書きにはなりません。素体(starter)のアイテムを取込ツールで入れてから初期の見た目を選んでください。
      </p>
    </section>
  );
}

interface EditorProps {
  readonly initial: CharacterDef;
  readonly isNew?: boolean;
  readonly onCancel?: () => void;
  readonly store: CatalogStore;
  readonly editable: boolean;
  readonly busy: boolean;
  readonly itemsById: ReadonlyMap<string, ItemDef>;
  readonly contentBase: string | null;
  readonly onSave: (def: CharacterDef) => Promise<void>;
}

type WordKind = 'prefixes' | 'nouns';
const WORD_ID_PREFIX: Readonly<Record<WordKind, string>> = { prefixes: 'prefix', nouns: 'noun' };
const WORD_LABELS: Readonly<Record<WordKind, string>> = {
  prefixes: '前につくことば(prefixes)',
  nouns: '名前のことば(nouns)',
};

function CharacterEditor({
  initial,
  isNew = false,
  onCancel,
  store,
  editable,
  busy,
  itemsById,
  contentBase,
  onSave,
}: EditorProps) {
  const [working, setWorking] = useState<CharacterDef>(initial);
  const [addSlot, setAddSlot] = useState('');
  const result = useMemo(
    () => validateDrafts(store.drafts, { character: working }),
    [store.drafts, working],
  );
  const issues = issuesFor(result, 'character', working.id);
  const rig = RIGS[working.rigType];
  const items = useMemo(() => [...itemsById.values()], [itemsById]);
  const missingSlots = rig.slots.filter(
    (slot) => !working.slots.some((existing) => existing.id === slot.id),
  );

  function update(patch: Partial<CharacterDef>): void {
    setWorking((current) => ({ ...current, ...patch }));
  }

  function updateSlot(index: number, patch: Partial<SlotDef>): void {
    update({ slots: working.slots.map((slot, i) => (i === index ? { ...slot, ...patch } : slot)) });
  }

  function moveSlot(index: number, delta: number): void {
    const target = index + delta;
    if (target < 0 || target >= working.slots.length) return;
    const slots = [...working.slots];
    const [moved] = slots.splice(index, 1);
    if (moved) slots.splice(target, 0, moved);
    update({ slots });
  }

  function removeSlot(index: number): void {
    const slot = working.slots[index];
    if (!slot) return;
    const equipped = { ...working.defaultLook.equipped };
    delete equipped[slot.id];
    update({
      slots: working.slots.filter((_, i) => i !== index),
      defaultLook: { ...working.defaultLook, equipped },
    });
  }

  function setLook(slotId: string, itemId: string): void {
    const equipped = { ...working.defaultLook.equipped };
    if (itemId === '') delete equipped[slotId];
    else equipped[slotId] = itemId;
    update({ defaultLook: { ...working.defaultLook, equipped } });
  }

  function setBase(base: string): void {
    const look: CharacterLook =
      base === '' ? { equipped: working.defaultLook.equipped } : { ...working.defaultLook, base };
    update({ defaultLook: look });
  }

  function updateWords(kind: WordKind, words: readonly LocalizedWord[]): void {
    update({ nameParts: { ...working.nameParts, [kind]: words } });
  }

  return (
    <form
      className="editor"
      aria-label="キャラクターの編集"
      onSubmit={(event) => {
        event.preventDefault();
        void onSave(working);
      }}
    >
      <h3>
        {isNew ? '新しいキャラクター' : 'キャラクター'} <span className="mono">{working.id}</span>
      </h3>
      <ReadOnlyNote editable={editable} />
      <IssueList issues={issues} />
      <fieldset className="editor__fields" disabled={!editable || busy}>
        <div className="meta-grid">
          <span className="meta-grid__key">ID</span>
          <span className="mono">{working.id}</span>
          <span className="meta-grid__key">リグ</span>
          <span className="mono">{working.rigType}</span>
        </div>
        <label className="field">
          <span className="field__label">名前(ja。ひらがな優先)</span>
          <input
            className="input"
            aria-label="名前"
            value={working.name.ja ?? ''}
            onChange={(event) => {
              update({ name: { ...working.name, ja: event.target.value } });
            }}
          />
        </label>
        <label className="field">
          <span className="field__label">状態</span>
          <select
            className="select"
            aria-label="状態"
            value={working.status}
            onChange={(event) => {
              update({ status: event.target.value as CharacterDef['status'] });
            }}
          >
            {CHARACTER_STATUSES.map((status) => (
              <option key={status} value={status}>
                {CHARACTER_STATUS_LABELS[status]}
              </option>
            ))}
          </select>
        </label>

        <section className="card" aria-label="枠">
          <h3>枠(リグ {rig.type} の既定から選ぶ)</h3>
          <table className="table">
            <thead>
              <tr>
                <th>ID</th>
                <th>名前</th>
                <th>種類</th>
                <th>必須</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {working.slots.map((slot, index) => (
                <tr key={slot.id}>
                  <td className="mono">{slot.id}</td>
                  <td>
                    <input
                      className="input"
                      aria-label={`枠 ${slot.id} の名前`}
                      value={slot.name.ja ?? ''}
                      onChange={(event) => {
                        updateSlot(index, { name: { ...slot.name, ja: event.target.value } });
                      }}
                    />
                  </td>
                  <td>{slot.kind === 'base' ? '素体' : '装備'}</td>
                  <td>
                    <input
                      type="checkbox"
                      aria-label={`枠 ${slot.id} を必須にする`}
                      checked={slot.required}
                      disabled={slot.kind !== 'base'}
                      onChange={(event) => {
                        updateSlot(index, { required: event.target.checked });
                      }}
                    />
                  </td>
                  <td className="nowrap">
                    <button
                      className="button button--small"
                      type="button"
                      aria-label={`枠 ${slot.id} を上へ`}
                      onClick={() => {
                        moveSlot(index, -1);
                      }}
                    >
                      ↑
                    </button>
                    <button
                      className="button button--small"
                      type="button"
                      aria-label={`枠 ${slot.id} を下へ`}
                      onClick={() => {
                        moveSlot(index, 1);
                      }}
                    >
                      ↓
                    </button>
                    <button
                      className="button button--small"
                      type="button"
                      aria-label={`枠 ${slot.id} を外す`}
                      onClick={() => {
                        removeSlot(index);
                      }}
                    >
                      外す
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {missingSlots.length > 0 && (
            <div className="toolbar">
              <select
                className="select select--inline"
                aria-label="追加する枠"
                value={addSlot}
                onChange={(event) => {
                  setAddSlot(event.target.value);
                }}
              >
                <option value="">枠を追加…</option>
                {missingSlots.map((slot) => (
                  <option key={slot.id} value={slot.id}>
                    {slot.id}({slot.name.ja})
                  </option>
                ))}
              </select>
              <button
                className="button button--small"
                type="button"
                disabled={addSlot === ''}
                onClick={() => {
                  const slot = rig.slots.find((entry) => entry.id === addSlot);
                  if (slot) update({ slots: [...working.slots, slot] });
                  setAddSlot('');
                }}
              >
                追加
              </button>
            </div>
          )}
          <IssueList issues={fieldIssues(issues, 'slots')} />
        </section>

        <section className="card" aria-label="初期の見た目">
          <h3>初期の見た目(defaultLook。starter から選ぶ)</h3>
          {rig.usesBases && (
            <label className="field">
              <span className="field__label">素体(base)</span>
              <select
                className="select"
                aria-label="素体"
                value={working.defaultLook.base ?? ''}
                onChange={(event) => {
                  setBase(event.target.value);
                }}
              >
                <option value="">(なし)</option>
                {(working.bases ?? []).map((base) => (
                  <option key={base.id} value={base.id}>
                    {base.emoji} {base.name.ja}
                  </option>
                ))}
              </select>
            </label>
          )}
          {working.slots
            .filter((slot) => slot.kind === 'base')
            .map((slot) => (
              <label className="field" key={slot.id}>
                <span className="field__label">
                  {slot.name.ja}({slot.id}){slot.required ? ' 必須' : ''}
                </span>
                <select
                  className="select"
                  aria-label={`初期の見た目 ${slot.id}`}
                  value={working.defaultLook.equipped[slot.id] ?? ''}
                  onChange={(event) => {
                    setLook(slot.id, event.target.value);
                  }}
                >
                  <option value="">(なし)</option>
                  {starterItems(items, working.id, slot.id).map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name.ja || item.id}
                    </option>
                  ))}
                </select>
              </label>
            ))}
          <IssueList issues={fieldIssues(issues, 'defaultLook')} />
          {working.rigType === 'pixel-layered' && (
            <PixelPreview
              contentBase={contentBase}
              equipped={working.defaultLook.equipped}
              itemsById={itemsById}
            />
          )}
        </section>

        <section className="card" aria-label="名前の部品">
          <h3>プレイヤーが選べる名前の部品(nameParts)</h3>
          {(['prefixes', 'nouns'] as const).map((kind) => {
            const words = working.nameParts[kind];
            return (
              <div className="field" key={kind}>
                <span className="field__label">{WORD_LABELS[kind]}</span>
                <table className="table" aria-label={WORD_LABELS[kind]}>
                  <tbody>
                    {words.map((word, index) => (
                      <tr key={word.id}>
                        <td className="mono">{word.id}</td>
                        <td>
                          <input
                            className="input"
                            aria-label={`${kind} ${word.id}`}
                            value={word.text.ja ?? ''}
                            onChange={(event) => {
                              updateWords(
                                kind,
                                words.map((w, i) =>
                                  i === index
                                    ? { ...w, text: { ...w.text, ja: event.target.value } }
                                    : w,
                                ),
                              );
                            }}
                          />
                        </td>
                        <td>
                          <button
                            className="button button--small"
                            type="button"
                            aria-label={`${kind} ${word.id} を削除`}
                            onClick={() => {
                              updateWords(
                                kind,
                                words.filter((_, i) => i !== index),
                              );
                            }}
                          >
                            削除
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <button
                  className="button button--small"
                  type="button"
                  aria-label={`${kind} に追加`}
                  onClick={() => {
                    updateWords(kind, [
                      ...words,
                      {
                        id: nextWordId(
                          words,
                          WORD_ID_PREFIX[kind],
                          initial.nameParts[kind].map((word) => word.id),
                        ),
                        text: { ja: '' },
                      },
                    ]);
                  }}
                >
                  追加
                </button>
              </div>
            );
          })}
          <IssueList issues={fieldIssues(issues, 'nameParts')} />
        </section>
      </fieldset>
      {editable && (
        <div className="actions">
          <button className="button button--primary" type="submit" disabled={busy}>
            {busy ? '保存中…' : '保存'}
          </button>
          {isNew && onCancel && (
            <button className="button" type="button" onClick={onCancel}>
              取り消し
            </button>
          )}
        </div>
      )}
    </form>
  );
}
